import './ProductTile.scss';

import { b2x } from '@b2x/react/src';
import classnames from 'classnames';
import React from 'react';
import { SwiperSlide } from 'swiper/react';

import { Button, ButtonVariant } from './Button';
import { CustomRadio } from './CustomRadio';
import { t } from './i18n/i18n';
import { IconName, IconSize } from './Icon';
import { SkuAttributeFormGroup } from './SkuAttributeFormGroup';
import { Slider } from './Slider';
import { SliderButton } from './SliderButton';

export type ProductTileProps = b2x.ProductTileProps;

export const ProductTile = ({ enableHover = true, ...otherProps }: ProductTileProps) => {
  return <b2x.ProductTile className={'card border-0 mb-3'} enableHover={enableHover} {...otherProps} />;
};

export type ProductTileBasicInfoProps = b2x.ProductTileBasicInfoProps;

export const ProductTileBasicInfo = (props: ProductTileBasicInfoProps) => (
  <b2x.ProductTileBasicInfo {...props} customComponent={CustomProductTileBasicInfo} />
);

const CustomProductTileBasicInfo = ({
  handleLinkClick,
  product,
  productsPerRow,
  sku,
}: b2x.ProductTileBasicInfoProps) => {
  const {
    bestPriceDiscountPercentage,
    bestPriceValue,
    crossedOutPrice,
    discountPercentageDecimal,
    isPriceInRange,
    price,
  } = b2x.usePrice(product.priceRange, sku?.price);

  const { stickersProductWithoutImage } = b2x.useStickersProduct({
    product: product,
  });

  return (
    <div className="product-tile-basic-info">
      <div className={classnames('card-header product-image p-0 border-0 bg-lighter')}>
        <b2x.router.Link onClick={handleLinkClick} to={product.url}>
          <b2x.EqualHeightElement name={`product-image-${productsPerRow}`}>
            <div className="d-flex align-items-center h-100">
              <b2x.Image
                alt={product.name}
                aspectRatio={b2x.appConfig.productImageAspectRatio}
                className="card-img-top"
                fluid
                format={800}
                src={product.image?.src}
              />
            </div>
          </b2x.EqualHeightElement>
        </b2x.router.Link>
        <div className="product-action">
          {b2x.appConfig.enableWishlist && (
            <b2x.WishlistButtonHelper product={product} sku={sku}>
              {({ handleWishlistButtonClick, inWishlist }) => (
                <Button
                  className=""
                  iconEnd={{ name: inWishlist ? 'wishlist-full' : 'wishlist', size: 30 }}
                  onClick={handleWishlistButtonClick}
                  size="large"
                  type="button"
                  variant="blank"
                />
              )}
            </b2x.WishlistButtonHelper>
          )}
        </div>
        {(discountPercentageDecimal ||
          product.tags?.includes('EQUESTRO_NEW') ||
          (stickersProductWithoutImage && stickersProductWithoutImage.length > 0)) && (
          <b2x.Div className="tile-stickers">
            <b2x.Ul display={'flex'} flexWrap={'wrap'} gap={1} margin={0} paddingBottom={3} paddingX={{ md: 3, xs: 2 }}>
              {discountPercentageDecimal && (
                <b2x.Li
                  className="sticker-promo"
                  marginStart={{ md: -3, xs: -2 }}
                >{`-${discountPercentageDecimal}%`}</b2x.Li>
              )}
              {product.tags?.includes('EQUESTRO_NEW') && (
                <b2x.Li className="sticker-new">{t('misc.newProductSticker')}</b2x.Li>
              )}
              {stickersProductWithoutImage?.map((sticker) => (
                <b2x.Li
                  className={classnames(sticker.code)}
                  key={sticker.code}
                  style={{
                    backgroundColor: sticker.backgroundColor,
                    border: sticker.borderColor ? `1px solid ${sticker.borderColor}` : undefined,
                    color: sticker.textColor,
                  }}
                >
                  {sticker.label}
                </b2x.Li>
              ))}
            </b2x.Ul>
          </b2x.Div>
        )}
      </div>
      <div className="card-body border-0 p-2">
        <div className="d-flex flex-column flex-md-row justify-content-between mb-1">
          <div className="product-title">
            <b2x.EqualHeightElement name={`product-row-${productsPerRow}`}>
              <h6 className="card-title fw-light mb-0">
                <b2x.router.Link className="text-decoration-none text-black" onClick={handleLinkClick} to={product.url}>
                  {b2x.formatHtml(product.name)}
                </b2x.router.Link>
              </h6>
            </b2x.EqualHeightElement>
          </div>
          <b2x.PriceBlock
            alignItems={{ md: 'end', xs: 'center' }}
            classNames={{ crossedOutPrice: 'small', price: 'small' }}
            direction={{ md: 'column', xs: 'row' }}
            gap={{ md: 0, xs: 2 }}
            hiddenDiscountPercentage
            priceHelper={{
              bestPriceDiscountPercentage: bestPriceDiscountPercentage,
              bestPriceValue: bestPriceValue,
              crossedOutPrice: crossedOutPrice,
              isPriceInRange: isPriceInRange,
              price: price,
            }}
          />
        </div>
        <b2x.Row className="justify-content-between align-items-start" gap={0}>
          {!product.giftCard && (
            <b2x.Col size={{ md: 'auto', xs: 12 }}>
              <div className="colors extra-small text-dark">
                {t('product.colorsCount', { count: (product.variantProducts?.length ?? 0) + 1 })}
              </div>
            </b2x.Col>
          )}
          {b2x.appConfig.enableBestPrice && (
            <b2x.Col size={{ md: '', xs: 12 }}>
              <div className="extra-small ps-md-3">
                <b2x.BestPrice
                  priceHelper={{
                    bestPriceDiscountPercentage: bestPriceDiscountPercentage,
                    bestPriceValue: bestPriceValue,
                    crossedOutPrice: crossedOutPrice,
                    price: price,
                  }}
                />
              </div>
            </b2x.Col>
          )}
        </b2x.Row>
      </div>
    </div>
  );
};

export type ProductTileExtraInfoProps = b2x.ProductTileExtraInfoProps;

export const ProductTileExtraInfo = (props: b2x.ProductTileExtraInfoProps) => (
  <b2x.ProductTileExtraInfo {...props} customComponent={CustomProductTileExtraInfo} />
);

const CustomProductTileExtraInfo = ({
  fieldsHelper,
  product,
  selectedProductVariant,
  selectedSku,
}: ProductTileExtraInfoProps) => {
  const { inWishlist } = b2x.useWishlistButtonHelper({
    product: selectedProductVariant ?? product,
    sku: selectedSku,
  });
  const colorsSliderNextButton = React.useRef<HTMLButtonElement>(null);
  const colorsSliderPrevButton = React.useRef<HTMLButtonElement>(null);
  const currentBreakpoint = b2x.useBreakpoint();
  return (
    <div className="product-tile-extra-info p-2">
      {!product.giftCard && (
        <div className="card-footer border-0 p-0 bg-white">
          {fieldsHelper.productVariantsFieldSet.productVariants.formFields.length > 1 && (
            <fieldset>
              <legend className="visually-hidden">{fieldsHelper.productVariantsFieldSet.legend}</legend>
              <b2x.FormGroup {...fieldsHelper.productVariantsFieldSet.productVariants.formGroup} label={undefined}>
                <div className="d-flex">
                  <SliderButton direction="left" innerRef={colorsSliderPrevButton} size="small" />
                  <Slider
                    navigation
                    responsive={{}}
                    slideContentCentered
                    sliderNextEl={colorsSliderNextButton}
                    sliderPrevEl={colorsSliderPrevButton}
                    slidesPerView={'auto'}
                    spaceBetween={15}
                  >
                    {fieldsHelper.productVariantsFieldSet.productVariants.formFields.map((formField) => (
                      <SwiperSlide key={formField.productVariant.id}>
                        <b2x.Radio {...formField.radio} inline>
                          <CustomRadio imageUrl={formField.productVariant.image?.src} />
                        </b2x.Radio>
                      </SwiperSlide>
                    ))}
                  </Slider>
                  <SliderButton direction="right" innerRef={colorsSliderNextButton} size="small" />
                </div>
              </b2x.FormGroup>
            </fieldset>
          )}
          {fieldsHelper.skuAttributes.map(({ attribute, formGroup, radios }, index) => (
            <SkuAttributeFormGroup
              attribute={attribute}
              formGroup={formGroup}
              index={index}
              key={attribute.title}
              radios={radios}
              sliderResponsive={{}}
              slidesPerView={'auto'}
              spaceBetween={15}
            />
          ))}
        </div>
      )}
      {selectedSku?.state !== 'AVAILABLE' && b2x.appConfig.skusNotAvailable === 'ADD-TO-WISHLIST' && (
        <div className="mb-2">
          <b className="small "> {inWishlist ? t('misc.availabilityNotification') : t('misc.productNotAvailable')}</b>
        </div>
      )}
      {!product.giftCard && (
        <div className="d-grid">
          <b2x.AddToCartFormButton<ButtonVariant, IconName, IconSize>
            availabilityEmailNotificationButton={{
              label: inWishlist
                ? t('form.addToCartForm.buttons.availabilityEmailNotification.remove.label')
                : b2x.untilBreakpoint('xs', currentBreakpoint)
                  ? t('form.addToCartForm.buttons.availabilityEmailNotification.addShort.label')
                  : t('form.addToCartForm.buttons.availabilityEmailNotification.addLong.label'),
            }}
            fieldsHelper={fieldsHelper}
            product={product}
            selectedSku={selectedSku}
          ></b2x.AddToCartFormButton>
        </div>
      )}
      {/* <div className="d-grid">
        <b2x.AddToCartFormButton<ButtonVariant, IconName, IconSize>
          availabilityEmailNotificationButton={{ className: 'text-uppercase' }}
          fieldsHelper={fieldsHelper}
          selectedSku={selectedSku}
          submitButton={{ className: 'text-uppercase' }}
        />
      </div> */}
    </div>
  );
};
